import { v4 as uuidv4 } from 'uuid';
import {
    Commit,
    Dispatch,
} from 'vuex';

export type Alert = {
    type: 'succes' | 'info' | 'warning';
    uuid: string;
    text: string;
    expires: number;
    link?: string;
    linkText?: string;
    onRemove: () => void;
    remove: () => void;
    onLinkClick: () => void;
    persistOnLinkClick?: boolean;
    withCloseIcon?: boolean;
};
export interface AlertsState {
    alertStack: Alert[];
}

const DEFAULT_DURATION = 5000;
const PUSH_ALERT = 'PUSH_ALERT';
const REMOVE_ALERT = 'REMOVE_ALERT';
const ON_LINK_CLICK = 'ON_LINK_CLICK';
const CLEAR_EXPIRED_ALERTS = 'CLEAR_EXPIRED_ALERTS';

function defaultState(): AlertsState {
    return {
        alertStack: [],
    };
}

const removeItemFromState = async (state: AlertsState, id: string) => {
    const index = state.alertStack.findIndex(({ uuid }) => uuid === id);
    const alert = state.alertStack[index];
    await alert.onRemove?.();
    state.alertStack = [...state.alertStack.slice(0, index), ...state.alertStack.slice(index + 1)];
    return state;
};
export const createAlertsModule = (uuidGenerator: () => string = uuidv4) => {
    return {
        namespaced: true,

        state: defaultState(),

        mutations: {
            [PUSH_ALERT]: (state: AlertsState, alertMessage: Alert) => {
                state.alertStack.push(alertMessage);
                return state;
            },
            [REMOVE_ALERT]: (state: AlertsState, id: string) => {
                return removeItemFromState(state, id);
            },
            [ON_LINK_CLICK]: (state: AlertsState, id: string) => {
                const alert = state.alertStack.find(
                    ({ uuid, persistOnLinkClick }) => uuid === id && persistOnLinkClick,
                );
                if (alert) {
                    return removeItemFromState(state, id);
                }
                return state;
            },
            [CLEAR_EXPIRED_ALERTS]: (state: AlertsState) => {
                const { expiredAlerts, filteredExpiredAlerts } = state.alertStack.reduce(
                    (
                        acc: {
                            expiredAlerts: Alert[];
                            filteredExpiredAlerts: Alert[];
                        },
                        msg: Alert,
                    ) => {
                        if (msg.expires < Date.now()) {
                            acc.expiredAlerts.push(msg);
                        } else {
                            acc.filteredExpiredAlerts.push(msg);
                        }
                        return acc;
                    },
                    { expiredAlerts: [] as Alert[], filteredExpiredAlerts: [] as Alert[] },
                );
                if (expiredAlerts.length) {
                    state.alertStack = filteredExpiredAlerts;
                    expiredAlerts.forEach((msg: Alert) => {
                        msg.onRemove?.();
                    });
                    return state;
                }
            },
        },

        getters: {
            alerts: (state: AlertsState) => state.alertStack,
        },

        actions: {
            pushAlert: (
                { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
                alertMessage: Omit<Alert, 'uuid' | 'onLinkClick' | 'remove'>,
            ) => {
                const uuid = uuidGenerator();
                const expires
                    = Number(new Date(alertMessage.expires)) || Date.now() + DEFAULT_DURATION;
                const timeout = expires - Date.now();
                if (timeout < 0) {
                    return alertMessage.onRemove?.();
                }
                setTimeout(() => {
                    commit(CLEAR_EXPIRED_ALERTS);
                }, timeout);
                commit(PUSH_ALERT, {
                    uuid,
                    expires,
                    text: alertMessage.text,
                    link: alertMessage.link,
                    linkText: alertMessage.linkText,
                    type: alertMessage.type,
                    onRemove: alertMessage.onRemove,
                    withCloseIcon: alertMessage.withCloseIcon,
                    onLinkClick: () => {
                        if (alertMessage.persistOnLinkClick) {
                            return;
                        }
                        dispatch('removeAlert', uuid);
                    },
                    remove: () => dispatch('removeAlert', uuid),
                });
            },
            removeAlert: ({ commit }: { commit: Commit }, id: string) => {
                commit(REMOVE_ALERT, id);
            },
            onLinkClick: ({ commit }: { commit: Commit }, id: string) => {
                commit(ON_LINK_CLICK, id);
            },
        },
    };
};

export type AlertsStore = ReturnType<typeof createAlertsModule>;

export default createAlertsModule();
